import { Button, Card, CardContent } from "@devhop/ui";
import { Pencil } from "lucide-react";
import type { LeaveEntitlementListItem } from "@/modules/hr/leave/domain/types";

export type LeaveEntitlementByEmployeeCardProps = {
  entitlements: LeaveEntitlementListItem[];
  year: number;
  isLoading?: boolean;
  onEdit: (row: LeaveEntitlementListItem) => void;
  /** Optional empty message override */
  emptyMessage?: string;
};

export function LeaveEntitlementByEmployeeCard({
  entitlements,
  year,
  isLoading = false,
  onEdit,
  emptyMessage,
}: LeaveEntitlementByEmployeeCardProps) {
  const message = emptyMessage ?? `ບໍ່ພົບສິດລາຕໍ່ປີສຳລັບພະນັກງານນີ້ໃນປີ ${year}.`;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center text-muted-foreground">
            <span className="text-sm">ກຳລັງໂຫຼດ...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (entitlements.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          {message}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="ສິດລາຕໍ່ປະເພດ">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium" scope="col">
                  ປະເພດລາ
                </th>
                <th
                  className="px-4 py-3 text-right font-medium tabular-nums"
                  scope="col"
                >
                  ວັນມີສິດ
                </th>
                <th
                  className="px-4 py-3 text-right font-medium tabular-nums"
                  scope="col"
                >
                  ໃຊ້ແລ້ວ
                </th>
                <th
                  className="px-4 py-3 text-right font-medium tabular-nums"
                  scope="col"
                >
                  ເກັບເຂົ້າມາ
                </th>
                <th
                  className="px-4 py-3 text-right font-medium tabular-nums"
                  scope="col"
                >
                  ຄົງເຫຼືອ
                </th>
                <th className="w-24 px-4 py-3" scope="col">
                  <span className="sr-only">ການດຳເນີນການ</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {entitlements.map((e) => (
                <tr
                  key={e.id}
                  className="border-border/80 border-b transition-colors last:border-b-0 hover:bg-muted/30"
                >
                  <td className="px-4 py-3">
                    <span className="font-medium">{e.leaveTypeName}</span>
                    {e.leaveTypeCode ? (
                      <span className="text-muted-foreground text-xs">
                        {" "}
                        ({e.leaveTypeCode})
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {e.totalDays}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {e.usedDays}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {e.carriedOverDays}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={
                        e.remainingDays < 0
                          ? "font-medium text-destructive tabular-nums"
                          : "font-medium text-primary tabular-nums"
                      }
                    >
                      {e.remainingDays}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => onEdit(e)}
                      aria-label={`ແກ້ໄຂສິດລາ ${e.leaveTypeName}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      ແກ້ໄຂ
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
